import { ColocaLogo } from "@/shared/components/ColocaLogo";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardStatementCard } from "../../types/card-statement.types";

export interface StatementMastheadProps {
  variant: "full" | "compact";
  card: CardStatementCard;
  statementNumber: string;
  periodLabel: string;
  issuedAtLabel: string;
}

/**
 * The flat brand background sits under the gradient so a browser that drops gradients when printing still
 * gets a dark green band with legible white text.
 */
const BRAND_BAND = "bg-brand-900 bg-gradient-to-br from-brand-800 via-brand-900 to-brand-950 text-white";

export const StatementMasthead = ({
  variant,
  card,
  statementNumber,
  periodLabel,
  issuedAtLabel,
}: StatementMastheadProps) => {
  const { t } = useTranslations("cards");
  const maskedNumber = `•••• ${card.last_4}`;

  if (variant === "compact") {
    return (
      <header className={`${BRAND_BAND} flex items-center justify-between gap-4 px-10 py-3`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <ColocaLogo variant="white" className="[&>img]:h-4 [&>img]:w-auto" />
          <span className="truncate text-[10px] text-white/70">
            {t("statement_document_title")} · {t("statement_continued")}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3 font-mono text-[10px] tabular-nums text-white/80">
          <span>{maskedNumber}</span>
          <span aria-hidden="true" className="text-white/40">
            ·
          </span>
          <span>{statementNumber}</span>
        </div>
      </header>
    );
  }

  return (
    <header className={`${BRAND_BAND} px-10 pb-7 pt-8`}>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <ColocaLogo variant="white" className="[&>img]:h-7 [&>img]:w-auto" />
          <p className="mt-5 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/60">
            {t("statement_document_title")}
          </p>
          <h1 className="mt-1 truncate text-2xl font-semibold leading-tight">
            {card.alias || t("statement_card_label")}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-white/80">
            <span className="font-mono tabular-nums">{maskedNumber}</span>
            <span aria-hidden="true" className="text-white/40">
              ·
            </span>
            <span>{t(`product_${card.card_type.toLowerCase()}` as never)}</span>
            {card.expiration_date && (
              <>
                <span aria-hidden="true" className="text-white/40">
                  ·
                </span>
                <span className="font-mono tabular-nums">{card.expiration_date}</span>
              </>
            )}
          </div>
        </div>

        <dl className="shrink-0 space-y-2.5 text-right text-[9px]">
          <div>
            <dt className="uppercase tracking-wider text-white/55">{t("statement_period_label")}</dt>
            <dd className="mt-0.5 font-mono text-[11px] tabular-nums text-white">{periodLabel}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-wider text-white/55">{t("statement_issued_at_label")}</dt>
            <dd className="mt-0.5 font-mono text-[11px] tabular-nums text-white">{issuedAtLabel}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-wider text-white/55">{t("statement_number_label")}</dt>
            <dd className="mt-0.5 font-mono text-[11px] tabular-nums text-white">{statementNumber}</dd>
          </div>
        </dl>
      </div>
    </header>
  );
};
